import { useCallback, useEffect, useState } from "react"
import { GetApp } from "../../App"
import { FindShip } from "../../App/Context"
import { logger } from "../../App/Logger"
import { Block, BlockRow, BlockField } from "../../Components/Block"
import { LocalizedMessage } from "../../Components/LocalizedMessage"
import { ApiClient, ShipApi } from "../../Types"
import { IShipResyncStatusResponse } from "../../Types/Generated/LogbookApp/ShipApi"
import { GetAppView } from "../AppView"
import { NMEAConfigurationView } from "../NMEAConfigurationView"
import { OverlayView } from "../OverlayView"
import { ShipSettingsView } from "../ShipSettingsView"
import { GetShipView } from "./Context"
import { ResyncConfirmView, ResyncStatusView } from "./ResyncViews"
import { ShipRoutes } from "./Router"
import { UserSwitchView } from "./UserSwitchView"

export const ShipOverlayContentViewData = {
  reset(api: ApiClient, action: "local" | "cloud") {
    const ships = new ShipApi(api).ships;
    switch (action) {
      case "cloud":
        return ships.resetCloud({ params: {} }).call();
      case "local":
        return ships.resetLocal({ params: {} }).call();
    }
  },
  // Who holds the claim RIGHT NOW, straight from the cloud. Deliberately not read from
  // the ship in `FindShip()`: that is the local mirror, it arrives by sync, and this
  // dialog is only ever open because sync is broken (#1453).
  status(api: ApiClient) {
    return new ShipApi(api).ships.resyncStatus({ params: {} }).call();
  },
};

export enum ShipViewOverlayContent {
  none = "none",
  importTrips = "importTrips",
  nmeaSettings = "nmeaSettings",
  resync = "resync",
  shipSettings = "shipSettings",
  userSwitch = "userSwitch",
}

export interface ShipViewOverlayOptions {
  shipId?: number;
}

interface ShipOverlayContentViewProps {
}

/**
 * Body of the resync overlay.
 *
 * A real component rather than an inline `render` callback: `OverlayView` calls each
 * definition's `render()` inline in its own body, so hooks written there would join
 * OverlayView's hook list and change its length whenever the active overlay — or, here,
 * the local confirm step — changes. React answers that with the "Rendered more hooks than
 * during the previous render" crash (#1267), so the state and the scroll hook live in the
 * component that owns them.
 */
function ShipResyncOverlayBody() {
  const app = GetApp();
  const ship = FindShip();
  const { navigateShip, resetShipOverlay } = GetShipView();
  const t = app.getT("ShipView");
  const [confirmAction, setConfirmAction] = useState<"local" | "cloud" | null>(null);
  const [status, setStatus] = useState<IShipResyncStatusResponse | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Two separate errors on purpose: "I could not read the claim state" belongs to the
  // landing view, "the reset itself failed" to the confirm step. Sharing one would let
  // the status re-read that follows a failed reset wipe the message explaining it.
  const [statusError, setStatusError] = useState<string | undefined>(undefined);
  const [actionError, setActionError] = useState<string | undefined>(undefined);

  // Establish who holds the claim when the dialog opens. The mirror this used to gate
  // on (`ship.clientToken == undefined`) is delivered by sync, so in the very state
  // this dialog exists for it can say anything: it left the buttons disabled while the
  // cloud held no claim at all, and enabled for a ship another device had taken.
  const loadStatus = useCallback(async () => {
    setLoading(true);
    setStatusError(undefined);
    try {
      setStatus(await ShipOverlayContentViewData.status(app.apiClient));
    } catch (failure) {
      logger.debug("Failed to read the resync status", failure);
      setStatus(undefined);
      setStatusError(t("resync_status_failed"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const executeReset = async (action: "local" | "cloud") => {
    setBusy(true);
    setActionError(undefined);
    try {
      await ShipOverlayContentViewData.reset(app.apiClient, action);
    } catch (failure) {
      // #1453: this had no catch at all. `Client.call` rejects on any non-2xx, so a
      // failed reset skipped the overlay reset, the navigation AND the reload, and
      // surfaced nothing -- a dialog indistinguishable from a dead button. A refusal is
      // expected here whenever another device claimed the ship between the status read
      // and the confirm, so it has to be shown rather than swallowed.
      logger.debug("Failed to reset the ship connection", failure);
      const claimed = (failure as { httpStatus?: number } | undefined)?.httpStatus == 401;
      setActionError(claimed ? t("resync_reset_claimed") : t("resync_reset_failed"));
      setBusy(false);
      // Re-read the claim state so going back shows whatever is now true, rather than
      // the picture the user started from.
      loadStatus();
      return;
    }
    setBusy(false);
    resetShipOverlay();
    navigateShip(ShipRoutes.sync);
  };

  if (confirmAction) {
    return (
      <ResyncConfirmView
        action={confirmAction}
        shipName={ship?.name}
        busy={busy}
        error={actionError}
        onBack={() => { setActionError(undefined); setConfirmAction(null); }}
        onConfirm={() => executeReset(confirmAction)}
        t={t}
      />
    );
  }

  return (
    <ResyncStatusView
      status={status}
      shipName={ship?.name}
      error={statusError}
      loading={loading}
      onResetLocal={() => setConfirmAction("local")}
      onResetCloud={() => setConfirmAction("cloud")}
      onCancel={resetShipOverlay}
      t={t}
    />
  );
}

export function ShipOverlayContentView(_: ShipOverlayContentViewProps) {
  const app = GetApp();
  const ship = FindShip();
  const { connected: _connected, loadShips: _loadShips, ships: _ships } = GetAppView();
  const { navigateShip: _navigateShip, shipOverlay, shipOverlayOptions: _shipOverlayOptions, resetShipOverlay, setShipOverlay: _setShipOverlay } = GetShipView();
  const t = app.getT("ShipView");
  const _ta = app.getT("AccountSettingsView");

  return (
    <OverlayView overlay={shipOverlay} overlays={{
      [ShipViewOverlayContent.none]: {
        render: () => null,
      },

      // Outdated?
      [ShipViewOverlayContent.importTrips]: {
        close: true,
        title: t("import_trips_title"),
        render: () => {
          return (
            <div className="Overlay-body">
              <Block>
                <BlockRow>
                  <LocalizedMessage message={(m) => m.importTrips.text} replace={{ name: ship?.name || "" }} />
                </BlockRow>
                <BlockRow actions={true}>
                  <BlockField>
                    <button className="button" onClick={resetShipOverlay}>{t("import_trips_cancel")}</button>
                  </BlockField>
                  <BlockField>
                    <button className="button default" onClick={() => {
                      // TODO: Implement file picker and import logic

                      resetShipOverlay();
                    }}
                    >{t("import_trips_choose")}</button>
                  </BlockField>
                </BlockRow>
              </Block>
            </div>
          );
        },
      },
      [ShipViewOverlayContent.nmeaSettings]: {
        close: true,
        render: () => <NMEAConfigurationView />,
      },
      [ShipViewOverlayContent.resync]: {
        close: true, title: t("resync_title"), style: "alarm", className: "overlay-max-height",
        render: () => <ShipResyncOverlayBody />,
      },
      [ShipViewOverlayContent.shipSettings]: {
        close: true,
        render: () => <ShipSettingsView ship={ship} />,
      },
      [ShipViewOverlayContent.userSwitch]: {
        close: true,
        render: () => <UserSwitchView />,
      },
    }} resetOverlay={resetShipOverlay}
    />
  );
}
